import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import html2pdf from 'html2pdf.js';
import useStore from '../../store';
import './AdminLeaveBalanceVerification.css';
import '../Attendance/Attendance.css';

const CAL_STATUS_STYLE = {
  working:     { background: '#D1FAE5', color: '#10B981' },
  half_day:    { background: '#FEF3C7', color: '#F59E0B' },
  not_working: { background: '#FEE2E2', color: '#EF4444' },
};
const CAL_STATUS_TAG = { working: 'WORKING', half_day: 'HALF DAY', not_working: 'OFF' };
const CAL_STATUS_OPTIONS = [
  { value: 'working', label: 'Working', color: '#10B981' },
  { value: 'half_day', label: 'Half Day', color: '#F59E0B' },
  { value: 'not_working', label: 'Not Working', color: '#EF4444' },
];

// Builds one month's calendar cells (same shape/priority logic as the My
// Attendance page: Holiday > Leave > Attendance status).
const buildMonthCalendar = (year, month, attendanceForMonth, approvedLeaves, holidays) => {
  const firstDay = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const recMap = {};
  attendanceForMonth.forEach((r) => { recMap[r.date] = r; });

  const leaveMap = {};
  approvedLeaves.forEach((l) => {
    const d1 = new Date(l.start_date);
    const d2 = new Date(l.end_date);
    for (let d = new Date(d1); d <= d2; d.setDate(d.getDate() + 1)) {
      leaveMap[d.toISOString().split('T')[0]] = l;
    }
  });

  const holMap = {};
  holidays.forEach((h) => { holMap[h.date] = h; });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${pad2(month)}-${pad2(d)}`;
    cells.push({
      day: d,
      dateStr,
      record: recMap[dateStr] || null,
      leave: leaveMap[dateStr] || null,
      holiday: holMap[dateStr] || null,
    });
  }
  return cells;
};

// Groups a raw leave_type string into one of the three base categories
const getLeaveCategory = (leaveType) => {
  const normalized = String(leaveType || '').toLowerCase();
  if (normalized.includes('maternity') || normalized.includes('paternity')) return 'maternity';
  if (normalized.includes('sick')) return 'sick';
  if (normalized.includes('earned') || normalized.includes('vacation')) return 'earned';
  return 'other';
};

const isCancelRow = (leaveType) => /^cancel/i.test(leaveType || '');

// Short labels for the per-type leave breakdown (EL/SL, etc.) in the report.
const LEAVE_TYPE_SHORT_LABELS = {
  earned: 'EL',
  sick: 'SL',
  casual: 'CL',
  maternity: 'Maternity',
  paternity: 'Paternity',
  unpaid: 'Unpaid',
  birthday: 'Birthday',
  menstrual_leaves: 'Menstrual',
  compensation_leave: 'Comp',
  wfh: 'WFH',
  special: 'Special',
  wedding: 'Wedding',
  bereavement: 'Bereavement',
};
const formatLeaveType = (leaveType) => {
  const key = String(leaveType || '').toLowerCase();
  if (LEAVE_TYPE_SHORT_LABELS[key]) return LEAVE_TYPE_SHORT_LABELS[key];
  return leaveType ? leaveType.charAt(0).toUpperCase() + leaveType.slice(1) : '';
};

// Parses a master_data date string, which may be 'DD/MM/YY(YY)' or 'YYYY-MM-DD'.
const parseRecordDate = (value) => {
  if (!value) return null;
  const ddmmyy = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(value);
  if (ddmmyy) {
    const [, d, m, y] = ddmmyy;
    const year = y.length === 2 ? 2000 + parseInt(y, 10) : parseInt(y, 10);
    return new Date(year, parseInt(m, 10) - 1, parseInt(d, 10));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const pad2 = (n) => String(n).padStart(2, '0');
const toISODate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Computes the [startDate, endDate] (inclusive, YYYY-MM-DD) for a given
// period type anchored on a specific date, plus a human-readable label.
const computePeriodRange = (periodType, anchorDateStr) => {
  const anchor = anchorDateStr ? new Date(`${anchorDateStr}T00:00:00`) : new Date();
  const year = anchor.getFullYear();
  const month = anchor.getMonth();

  if (periodType === 'day') {
    return { startDate: toISODate(anchor), endDate: toISODate(anchor), label: toISODate(anchor) };
  }
  if (periodType === 'week') {
    const dayOfWeek = anchor.getDay();
    const diffToMonday = (dayOfWeek + 6) % 7;
    const monday = new Date(anchor);
    monday.setDate(anchor.getDate() - diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { startDate: toISODate(monday), endDate: toISODate(sunday), label: `Week of ${toISODate(monday)}` };
  }
  if (periodType === 'quarter') {
    const qStartMonth = Math.floor(month / 3) * 3;
    const start = new Date(year, qStartMonth, 1);
    const end = new Date(year, qStartMonth + 3, 0);
    return { startDate: toISODate(start), endDate: toISODate(end), label: `Q${Math.floor(month / 3) + 1} ${year}` };
  }
  if (periodType === 'half-year') {
    const hStartMonth = month < 6 ? 0 : 6;
    const start = new Date(year, hStartMonth, 1);
    const end = new Date(year, hStartMonth + 6, 0);
    return { startDate: toISODate(start), endDate: toISODate(end), label: `${month < 6 ? 'H1' : 'H2'} ${year}` };
  }
  if (periodType === 'year') {
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    return { startDate: toISODate(start), endDate: toISODate(end), label: `${year}` };
  }
  // month (default)
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  return { startDate: toISODate(start), endDate: toISODate(end), label: start.toLocaleString('default', { month: 'long', year: 'numeric' }) };
};

// Maps a record to its view tab: 'earned'/'sick'/'maternity' for normal
// entries, 'cancel-earned'/'cancel-sick'/'cancel-maternity' for cancellations
const getViewTabKey = (leaveType) => {
  const category = isCancelRow(leaveType)
    ? getLeaveCategory(leaveType.replace(/^cancel/i, ''))
    : getLeaveCategory(leaveType);
  return isCancelRow(leaveType) ? `cancel-${category}` : category;
};

const AdminLeaveBalanceVerification = () => {
  const { user } = useStore();
  const isAdmin = user?.role === 'admin';
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingUser, setEditingUser] = useState(null);
  const [editValues, setEditValues] = useState({
    earned: 0,
    sick: 0,
    maternity: 0,
    earnedRollover: 0,
    sickRollover: 0,
    maternityRollover: 0,
  });
  const [viewingUser, setViewingUser] = useState(null);
  const [viewRecords, setViewRecords] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewActiveTab, setViewActiveTab] = useState('earned');
  const [viewMonthFilter, setViewMonthFilter] = useState('');
  const [reportPeriodType, setReportPeriodType] = useState('month');
  const [reportAnchorDate, setReportAnchorDate] = useState(() => toISODate(new Date()));
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const reportOutputRef = useRef(null);

  useEffect(() => {
    fetchLeaveBalances();
  }, []);

  const fetchLeaveBalances = async () => {
    try {
      setLoading(true);
      
      // Fetch all leave balances from the new admin endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/leaves/all-balances?year=2026`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('aays_token')}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch leave balances: ${response.status}`);
      }

      const data = await response.json();
      
      // Group all records by user
      const userMap = {};
      if (data.data && Array.isArray(data.data)) {
        data.data.forEach((record) => {
          const userId = record.user_id;
          
          if (!userId) return;
          
          if (!userMap[userId]) {
            userMap[userId] = {
              user_id: userId,
              user_name: `${record.first_name} ${record.last_name}`.trim() || 'Unknown',
              email: record.email || '',
              employee_type: record.employee_type || 'full_time',
              earned: { allocated: 0, taken: 0, rollover: 0 },
              sick: { allocated: 0, taken: 0, rollover: 0 },
              maternity: { allocated: 0, taken: 0, rollover: 0 },
            };
          }

          const leaveType = record.leave_type?.toLowerCase() || '';
          
          if (leaveType.includes('earned')) {
            userMap[userId].earned = {
              allocated: parseFloat(record.total_allocated) || 0,
              taken: parseFloat(record.taken) || 0,
              rollover: parseFloat(record.rollover) || 0,
            };
          } else if (leaveType.includes('sick')) {
            userMap[userId].sick = {
              allocated: parseFloat(record.total_allocated) || 0,
              taken: parseFloat(record.taken) || 0,
              rollover: parseFloat(record.rollover) || 0,
            };
          } else if (leaveType.includes('maternity') || leaveType.includes('paternity')) {
            // Maternity and Paternity are separate leave_balances rows for every
            // employee — sum them into the merged "Maternity/Paternity" bucket
            // instead of letting the second one overwrite the first.
            userMap[userId].maternity = {
              allocated: userMap[userId].maternity.allocated + (parseFloat(record.total_allocated) || 0),
              taken: userMap[userId].maternity.taken + (parseFloat(record.taken) || 0),
              rollover: userMap[userId].maternity.rollover + (parseFloat(record.rollover) || 0),
            };
          }
        });
      }

      const users = Object.values(userMap).sort((a, b) =>
        a.user_name.localeCompare(b.user_name)
      );

      setLeaveBalances(users);
      setFilteredData(users);
      setError(null);
    } catch (err) {
      setError(err.message);
      setLeaveBalances([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (term, type) => {
    const filtered = leaveBalances.filter((user) => {
      const matchesSearch =
        user.user_name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term);
      const matchesType = !type || user.employee_type === type;
      return matchesSearch && matchesType;
    });

    setFilteredData(filtered);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    applyFilters(term, typeFilter);
  };

  const handleTypeFilterChange = (e) => {
    const type = e.target.value;
    setTypeFilter(type);
    applyFilters(searchTerm, type);
  };

  const handleSort = (column) => {
    let direction = 'asc';
    if (sortColumn === column && sortDirection === 'asc') {
      direction = 'desc';
    }

    const sorted = [...filteredData].sort((a, b) => {
      let aVal = '';
      let bVal = '';

      if (column === 'name') {
        aVal = a.user_name;
        bVal = b.user_name;
      } else if (column === 'email') {
        aVal = a.email;
        bVal = b.email;
      }

      if (direction === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });

    setFilteredData(sorted);
    setSortColumn(column);
    setSortDirection(direction);
  };

  const openViewModal = async (user) => {
    setViewingUser(user);
    setViewRecords([]);
    setViewActiveTab('earned');
    setViewMonthFilter('');
    setViewLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/admin/master-data?user_id=${user.user_id}&year=2026&limit=500`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('aays_token')}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch leave details: ${response.status}`);
      }
      const data = await response.json();
      const records = (data.data || []).filter(
        (r) => !/^birthday/i.test(r.leave_type)
      );
      setViewRecords(records);
    } catch (err) {
      console.error('Error fetching leave details:', err);
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewingUser(null);
    setViewRecords([]);
    setViewMonthFilter('');
    setShowReport(false);
    setReportData(null);
  };

  // Opens the same View modal but straight into the report tab, so admins can
  // jump directly to PDF generation without an extra click on "View" first.
  // Sets showReport in the same tick as viewingUser (skipping openViewModal's
  // records fetch, which isn't needed here) so the records tab never flashes.
  const openReportModal = (user) => {
    setViewingUser(user);
    setViewRecords([]);
    setViewActiveTab('earned');
    setViewMonthFilter('');
    setShowReport(true);
  };

  // Fetches a combined attendance + leave (taken/pending) + LOP report for the
  // currently-viewed employee over the selected period, reusing the same
  // backend logic as the Employee Report page.
  const generatePeriodReport = async () => {
    if (!viewingUser) return;
    const { startDate, endDate, label } = computePeriodRange(reportPeriodType, reportAnchorDate);
    setReportLoading(true);
    try {
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('aays_token')}` };
      // Holidays are year-scoped; fetch every year the selected period touches.
      const years = [...new Set([startDate.slice(0, 4), endDate.slice(0, 4)])];

      const [lopRes, detailsRes, ...holidayResponses] = await Promise.all([
        fetch(`${base}/users/reports/lop?startDate=${startDate}&endDate=${endDate}&userId=${viewingUser.user_id}`, { headers }),
        fetch(`${base}/users/reports/lop/${viewingUser.user_id}/details?startDate=${startDate}&endDate=${endDate}`, { headers }),
        ...years.map((year) => fetch(`${base}/holidays?year=${year}`, { headers })),
      ]);

      if (!lopRes.ok || !detailsRes.ok) {
        throw new Error('Failed to generate report');
      }

      const lopJson = await lopRes.json();
      const detailsJson = await detailsRes.json();
      const lopRow = (lopJson.data || [])[0] || null;
      const { leaves = [], attendance = [] } = detailsJson.data || {};
      const holidayJsons = await Promise.all(holidayResponses.map((r) => (r.ok ? r.json() : { data: [] })));
      const holidays = holidayJsons.flatMap((j) => j.data || []);

      setReportData({ startDate, endDate, label, lopRow, leaves, attendance, holidays });
      setShowReport(true);
    } catch (err) {
      toast.error(err.message || 'Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  const downloadReportPdf = () => {
    if (!reportOutputRef.current || !reportData) return;
    setPdfDownloading(true);
    const fileName = `${viewingUser.user_name.replace(/\s+/g, '-').toLowerCase()}-report-${reportData.startDate}-to-${reportData.endDate}.pdf`;
    html2pdf()
      .set({
        margin: 10,
        filename: fileName,
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      })
      .from(reportOutputRef.current)
      .save()
      .catch(() => toast.error('Failed to generate PDF'))
      .finally(() => setPdfDownloading(false));
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setEditValues({
      earned: user.earned.taken || 0,
      sick: user.sick.taken || 0,
      maternity: user.maternity.taken || 0,
      earnedRollover: user.earned.rollover || 0,
      sickRollover: user.sick.rollover || 0,
      maternityRollover: user.maternity.rollover || 0,
    });
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditValues({ earned: 0, sick: 0, maternity: 0, earnedRollover: 0, sickRollover: 0, maternityRollover: 0 });
  };

  const handleEditChange = (field, value) => {
    setEditValues({
      ...editValues,
      [field]: parseFloat(value) || 0,
    });
  };

  const saveChanges = async () => {
    if (!editingUser) return;

    try {
      // Builds an update payload for a category only if its taken and/or
      // rollover value actually changed from what was loaded into the modal.
      const buildUpdate = (category, leaveType) => {
        const takenChanged = editValues[category] !== editingUser[category].taken;
        const rolloverChanged = editValues[`${category}Rollover`] !== editingUser[category].rollover;
        if (!takenChanged && !rolloverChanged) return null;
        return {
          user_id: editingUser.user_id,
          leave_type: leaveType,
          taken: editValues[category],
          rollover: editValues[`${category}Rollover`],
        };
      };

      const updates = [
        buildUpdate('earned', 'earned'),
        buildUpdate('sick', 'sick'),
        buildUpdate('maternity', 'maternity'),
      ].filter(Boolean);

      if (updates.length === 0) {
        closeEditModal();
        return;
      }

      // Send updates to backend
      for (const update of updates) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/leaves/update`,
          {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('aays_token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(update),
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to update leave balance`);
        }
      }

      // Update local state
      const updatedBalances = leaveBalances.map((user) => {
        if (user.user_id === editingUser.user_id) {
          return {
            ...user,
            earned: { ...user.earned, taken: editValues.earned, rollover: editValues.earnedRollover },
            sick: { ...user.sick, taken: editValues.sick, rollover: editValues.sickRollover },
            maternity: { ...user.maternity, taken: editValues.maternity, rollover: editValues.maternityRollover },
          };
        }
        return user;
      });

      setLeaveBalances(updatedBalances);
      setFilteredData(updatedBalances);
      closeEditModal();
      toast.success('Leave balance updated');
    } catch (err) {
      toast.error(`Error saving changes: ${err.message}`);
    }
  };

  const getBalanceStatus = (allocated, taken, rollover) => {
    const available = allocated + rollover - taken;
    if (available < 0) return 'negative-balance';
    if (available < 2) return 'low-balance';
    return 'available';
  };

  const STATUS_BADGE = {
    available: { cls: 'badge-emerald', label: 'Available' },
    'low-balance': { cls: 'badge-amber', label: 'Low Balance' },
    'negative-balance': { cls: 'badge-rose', label: 'Negative Balance' },
  };

  const getOverallStatus = (row) => {
    const isContractor = String(row.employee_type || '').toLowerCase() === 'contractor';
    const statuses = [
      getBalanceStatus(row.earned.allocated, row.earned.taken, row.earned.rollover),
      // Sick leave is unlimited for regular employees by design (see DB check
      // constraint chk_taken_le_allocated) — only contractors (whose policies
      // grant 0 sick days) should ever show a sick-driven negative balance.
      ...(isContractor ? [getBalanceStatus(row.sick.allocated, row.sick.taken, row.sick.rollover)] : []),
      getBalanceStatus(row.maternity.allocated, row.maternity.taken, row.maternity.rollover),
    ];
    if (statuses.includes('negative-balance')) return 'negative-balance';
    if (statuses.includes('low-balance')) return 'low-balance';
    return 'available';
  };

  // The number shown alongside the status label — the most constrained
  // (lowest) available balance across Earned, Sick and Maternity/Paternity,
  // matching what actually drives the overall status above.
  const getMinAvailable = (row) => {
    const isContractor = String(row.employee_type || '').toLowerCase() === 'contractor';
    const available = (allocated, taken, rollover) => allocated + rollover - taken;
    return Math.min(
      available(row.earned.allocated, row.earned.taken, row.earned.rollover),
      // Sick leave is unlimited for non-contractors, so exclude it here too.
      isContractor ? available(row.sick.allocated, row.sick.taken, row.sick.rollover) : Infinity,
      available(row.maternity.allocated, row.maternity.taken, row.maternity.rollover)
    );
  };

  // Taken beyond what the policy allocates (+ rollover) is LOP-eligible — this is
  // what flags most contractor leave, since contractor policies allocate 0 days.
  const isOverPolicy = (category) => category.taken > category.allocated + category.rollover;

  // Sick leave is unlimited for regular employees by design — only flag it as
  // over-policy for contractors.
  const isSickOverPolicy = (row) =>
    String(row.employee_type || '').toLowerCase() === 'contractor' && isOverPolicy(row.sick);


  const exportToCSV = () => {
    const headers = [
      'Employee Name',
      'Email',
      'Earned Leave Taken',
      'Sick Leave Taken',
      'Maternity/Paternity Taken',
    ];

    const rows = filteredData.map((user) => [
      user.user_name,
      user.email,
      user.earned.taken,
      user.sick.taken,
      user.maternity.taken,
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leave-balance-verification-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="leave-verification">
      <div className="page-header">
        <div>
          <div className="page-title">Leave Balance Verification</div>
          <div className="page-subtitle">
            {isAdmin
              ? "Review and verify leave balances for all employees"
              : "Review and verify leave balances for your team"}
          </div>
        </div>
        <button className="btn btn-primary" onClick={exportToCSV} disabled={loading || filteredData.length === 0}>
          Export to CSV
        </button>
      </div>

      {error && (
        <div className="error-alert animate-in">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="card admin-filters animate-in">
        <div className="admin-filters-inner">
          <div className="filter-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name or email…"
              value={searchTerm}
              onChange={handleSearch}
              className="filter-search-input"
            />
          </div>
          <select className="form-select" style={{ width: 160 }} value={typeFilter} onChange={handleTypeFilterChange}>
            <option value="">All Types</option>
            <option value="full_time">Full Time</option>
            <option value="contractor">Contractor</option>
          </select>
        </div>
        <span className="filter-count">{filteredData.length} of {leaveBalances.length} employees</span>
      </div>

      <div className="card animate-in stagger-1" style={{ padding: 0 }}>
        {loading ? (
          <div style={{ padding: 24 }}>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8, borderRadius: 8 }} />
            ))}
          </div>
        ) : filteredData.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">No employees found</div>
            <div className="empty-state-text">Try adjusting your search.</div>
          </div>
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
            <table>
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')} className="sortable">
                    Employee {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="leave-type-header">Earned Leave</th>
                  <th className="leave-type-header">Sick Leave</th>
                  <th className="leave-type-header">Maternity/Paternity</th>
                  <th className="leave-type-header">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row) => {
                  const status = STATUS_BADGE[getOverallStatus(row)];
                  const minAvailable = getMinAvailable(row);
                  return (
                    <tr key={row.user_id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{row.user_name}</div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>{row.email}</div>
                      </td>
                      <td className={`number-cell ${isOverPolicy(row.earned) ? 'number-cell-lop' : ''}`} title={isOverPolicy(row.earned) ? 'Taken exceeds policy allocation + rollover — mark as LOP' : undefined}>{row.earned.taken}</td>
                      <td className={`number-cell ${isSickOverPolicy(row) ? 'number-cell-lop' : ''}`} title={isSickOverPolicy(row) ? 'Taken exceeds policy allocation + rollover — mark as LOP' : undefined}>{row.sick.taken}</td>
                      <td className={`number-cell ${isOverPolicy(row.maternity) ? 'number-cell-lop' : ''}`} title={isOverPolicy(row.maternity) ? 'Taken exceeds policy allocation + rollover — mark as LOP' : undefined}>{row.maternity.taken}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${status.cls}`} title="Lowest available balance across Earned, Sick and Maternity/Paternity leave">{status.label} ({minAvailable})</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => openViewModal(row)}
                            title="View leave dates taken"
                          >
                            View
                          </button>
                          {isAdmin && (
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => openEditModal(row)}
                              title="Edit leave taken values"
                            >
                              Edit
                            </button>
                          )}
                          {isAdmin && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => openReportModal(row)}
                              title="Generate a PDF leave/attendance report"
                            >
                              Generate Report (PDF)
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Leave Taken — {editingUser.user_name}</h3>
              <button className="btn btn-ghost btn-sm" onClick={closeEditModal}>✕</button>
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', gap: 14 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Earned Leave Taken</label>
                  <input
                    type="number"
                    step="0.5"
                    value={editValues.earned}
                    onChange={(e) => handleEditChange('earned', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Earned Leave Rollover</label>
                  <input
                    type="number"
                    step="0.5"
                    value={editValues.earnedRollover}
                    onChange={(e) => handleEditChange('earnedRollover', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 14 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Sick Leave Taken</label>
                  <input
                    type="number"
                    step="0.5"
                    value={editValues.sick}
                    onChange={(e) => handleEditChange('sick', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Sick Leave Rollover</label>
                  <input
                    type="number"
                    step="0.5"
                    value={editValues.sickRollover}
                    onChange={(e) => handleEditChange('sickRollover', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 14 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Maternity/Paternity Leave Taken</label>
                  <input
                    type="number"
                    step="0.5"
                    value={editValues.maternity}
                    onChange={(e) => handleEditChange('maternity', e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Maternity/Paternity Rollover</label>
                  <input
                    type="number"
                    step="0.5"
                    value={editValues.maternityRollover}
                    onChange={(e) => handleEditChange('maternityRollover', e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeEditModal}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveChanges}>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewingUser && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Leave Details — {viewingUser.user_name}</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {isAdmin && (
                  <button
                    className={`btn btn-sm ${showReport ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setShowReport((v) => !v)}
                  >
                    {showReport ? 'Back to Records' : 'Generate Report (PDF)'}
                  </button>
                )}
                <button className="btn btn-ghost btn-sm" onClick={closeViewModal}>✕</button>
              </div>
            </div>

            {!showReport && (
              <>
                <div className="view-tabs">
                  {[
                    { key: 'earned', label: 'Earned/Vacation' },
                    { key: 'sick', label: 'Sick Leave' },
                    { key: 'maternity', label: 'Maternity/Paternity' },
                    { key: 'cancel-earned', label: 'Cancel Earned/Vacation' },
                    { key: 'cancel-sick', label: 'Cancel Sick' },
                    { key: 'cancel-maternity', label: 'Cancel Maternity/Paternity' },
                  ].map((tab) => {
                    const count = viewRecords.filter((r) => getViewTabKey(r.leave_type) === tab.key).length;
                    return (
                      <button
                        key={tab.key}
                        className={`view-tab ${viewActiveTab === tab.key ? 'active' : ''}`}
                        onClick={() => setViewActiveTab(tab.key)}
                      >
                        {tab.label} <span className="view-tab-count">{count}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="view-month-filter">
                  <label htmlFor="view-month-filter-select">Month</label>
                  <select
                    id="view-month-filter-select"
                    className="form-select"
                    value={viewMonthFilter}
                    onChange={(e) => setViewMonthFilter(e.target.value)}
                  >
                    <option value="">All Months</option>
                    {MONTH_OPTIONS.map((month, index) => (
                      <option key={month} value={index}>{month}</option>
                    ))}
                  </select>
                </div>

                <div className="modal-body">
                  {viewLoading ? (
                    <div style={{ padding: '24px 0' }}>
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="skeleton" style={{ height: 36, marginBottom: 8, borderRadius: 8 }} />
                      ))}
                    </div>
                  ) : (() => {
                    const tabRecords = viewRecords
                      .filter((r) => getViewTabKey(r.leave_type) === viewActiveTab)
                      .filter((r) => {
                        if (viewMonthFilter === '') return true;
                        const date = parseRecordDate(r.start_date);
                        return date ? date.getMonth() === parseInt(viewMonthFilter, 10) : false;
                      });
                    const totalDays = tabRecords.reduce(
                      (sum, r) => sum + (parseFloat(r.day_count) || 0),
                      0
                    );
                    return tabRecords.length > 0 ? (
                      <>
                        <p className="view-tab-total">Total: {totalDays} day{totalDays === 1 ? '' : 's'}</p>
                        <div className="table-wrapper" style={{ border: 'none', maxHeight: 360 }}>
                          <table>
                            <thead>
                              <tr>
                                <th>Leave Type</th>
                                <th>Date(s)</th>
                                <th>Days</th>
                                <th>Remark</th>
                                <th>Approved</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tabRecords.map((record) => (
                                <tr key={record.id}>
                                  <td>{record.leave_type}</td>
                                  <td>
                                    {record.start_date === record.end_date
                                      ? record.start_date
                                      : `${record.start_date} to ${record.end_date}`}
                                  </td>
                                  <td>{record.day_count ?? '—'}</td>
                                  <td>{record.remark}</td>
                                  <td>{record.approved === true || record.approved === 'true' ? 'Yes' : 'No'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="empty-state">
                        <div className="empty-state-text">No leave records found in this category.</div>
                      </div>
                    );
                  })()}
                </div>
              </>
            )}

            {showReport && (
              <div className="leave-full-report">
                <div className="report-controls">
                  <div className="form-group">
                    <label className="form-label">Period Type</label>
                    <select className="form-select" value={reportPeriodType} onChange={(e) => setReportPeriodType(e.target.value)}>
                      <option value="day">Day</option>
                      <option value="week">Week</option>
                      <option value="month">Month</option>
                      <option value="quarter">Quarter</option>
                      <option value="half-year">Half-Yearly</option>
                      <option value="year">Year</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Anchor Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={reportAnchorDate}
                      onChange={(e) => setReportAnchorDate(e.target.value)}
                    />
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={generatePeriodReport} disabled={reportLoading}>
                    {reportLoading ? 'Generating…' : 'Generate'}
                  </button>
                  {reportData && (
                    <button className="btn btn-secondary btn-sm" onClick={downloadReportPdf} disabled={pdfDownloading}>
                      {pdfDownloading ? 'Preparing PDF…' : 'Download PDF'}
                    </button>
                  )}
                </div>

                {reportData && (() => {
                  const { label, startDate, endDate, lopRow, leaves, attendance } = reportData;
                  const approvedLeaves = leaves.filter((l) => l.status === 'approved');
                  const pendingLeaves = leaves.filter((l) => l.status === 'pending');
                  const rejectedLeaves = leaves.filter((l) => l.status === 'rejected');
                  const cancelledLeaves = leaves.filter((l) => l.status === 'cancelled');
                  const takenDays = approvedLeaves.reduce((sum, l) => sum + (parseFloat(l.days_requested) || 0), 0);
                  const pendingDays = pendingLeaves.reduce((sum, l) => sum + (parseFloat(l.days_requested) || 0), 0);
                  const attendanceByStatus = attendance.reduce((acc, a) => {
                    acc[a.status] = (acc[a.status] || 0) + 1;
                    return acc;
                  }, {});
                  // Breaks the approved total down by leave type (EL, SL, etc.)
                  const takenByType = approvedLeaves.reduce((acc, l) => {
                    const key = formatLeaveType(l.leave_type);
                    if (!acc[key]) acc[key] = { days: 0, count: 0 };
                    acc[key].days += parseFloat(l.days_requested) || 0;
                    acc[key].count += 1;
                    return acc;
                  }, {});

                  return (
                    <div className="report-output" ref={reportOutputRef}>
                      <h2>Employee Report — {viewingUser.user_name}</h2>
                      <p className="report-meta">{viewingUser.email} &middot; Period: {label} ({startDate} to {endDate})</p>

                      <div className="report-summary-grid">
                        <div className="report-summary-card">
                          <span>Leave Taken (Approved) — Total</span>
                          <strong>{takenDays.toFixed(2)} days ({approvedLeaves.length} requests)</strong>
                        </div>
                        {Object.entries(takenByType).map(([type, stats]) => (
                          <div className="report-summary-card" key={type}>
                            <span>{type} Taken (Approved)</span>
                            <strong>{stats.days.toFixed(2)} days ({stats.count} request{stats.count === 1 ? '' : 's'})</strong>
                          </div>
                        ))}
                        <div className="report-summary-card">
                          <span>Leave Pending</span>
                          <strong>{pendingDays.toFixed(2)} days ({pendingLeaves.length} requests)</strong>
                        </div>
                        <div className="report-summary-card">
                          <span>Rejected / Cancelled</span>
                          <strong>{rejectedLeaves.length} / {cancelledLeaves.length}</strong>
                        </div>
                        <div className="report-summary-card report-summary-card--danger">
                          <span>Loss of Pay (Period)</span>
                          <strong>{lopRow ? lopRow.lossOfPayDays.toFixed(2) : '—'} days</strong>
                        </div>
                      </div>

                      {lopRow && (
                        <div className="report-summary-grid">
                          <div className={`report-summary-card ${lopRow.elBalance >= 0 ? 'report-summary-card--success' : 'report-summary-card--danger'}`}>
                            <span>EL Balance</span>
                            <strong>{lopRow.elBalance.toFixed(2)}</strong>
                          </div>
                          <div className="report-summary-card">
                            <span>EL Taken (Period)</span>
                            <strong>{lopRow.elTakenSelected.toFixed(2)}</strong>
                          </div>
                          <div className="report-summary-card">
                            <span>SL Taken (Period)</span>
                            <strong>{lopRow.slTakenSelected.toFixed(2)}</strong>
                          </div>
                          <div className={`report-summary-card ${lopRow.employmentType === 'Contractor' ? 'report-summary-card--contractor' : 'report-summary-card--fulltime'}`}>
                            <span>Employment Type</span>
                            <strong>{lopRow.employmentType}</strong>
                          </div>
                        </div>
                      )}

                      <h3>Attendance Summary</h3>
                      <div className="report-summary-grid">
                        {Object.entries(attendanceByStatus).length > 0 ? (
                          Object.entries(attendanceByStatus).map(([status, count]) => {
                            const cardClass = status === 'working'
                              ? 'report-summary-card--success'
                              : status === 'not_working'
                                ? 'report-summary-card--danger'
                                : status === 'half_day'
                                  ? 'report-summary-card--contractor'
                                  : '';
                            return (
                              <div className={`report-summary-card ${cardClass}`} key={status}>
                                <span>{status}</span>
                                <strong>{count} day{count === 1 ? '' : 's'}</strong>
                              </div>
                            );
                          })
                        ) : (
                          <div className="report-summary-card"><span>No attendance records in this period</span></div>
                        )}
                      </div>

                      <h3>Leave Requests in Period</h3>
                      <div className="table-wrapper" style={{ border: 'none', maxHeight: 'none', overflow: 'visible' }}>
                        <table>
                          <thead>
                            <tr>
                              <th>Leave Type</th>
                              <th>Date(s)</th>
                              <th>Days</th>
                              <th>Status</th>
                              <th>Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {leaves.length === 0 ? (
                              <tr><td colSpan={5}>No leave requests in this period.</td></tr>
                            ) : leaves.map((l) => (
                              <tr key={l.id}>
                                <td>{l.leave_type}</td>
                                <td>{l.start_date === l.end_date ? l.start_date : `${l.start_date} to ${l.end_date}`}</td>
                                <td>{l.days_requested}</td>
                                <td>{l.status}</td>
                                <td>{l.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <h3>Attendance Records (Month-wise)</h3>
                      {(() => {
                        // Build one calendar per calendar-month the period spans,
                        // even if a month has zero attendance records.
                        const monthKeys = [];
                        const cursor = new Date(`${startDate}T00:00:00`);
                        const end = new Date(`${endDate}T00:00:00`);
                        while (cursor <= end) {
                          const key = `${cursor.getFullYear()}-${pad2(cursor.getMonth() + 1)}`;
                          if (!monthKeys.includes(key)) monthKeys.push(key);
                          cursor.setMonth(cursor.getMonth() + 1);
                          cursor.setDate(1);
                        }
                        const approvedLeavesForCalendar = leaves.filter((l) => l.status === 'approved');
                        const holidays = reportData.holidays || [];

                        return monthKeys.map((monthKey) => {
                          const [yearStr, monthStr] = monthKey.split('-');
                          const year = parseInt(yearStr, 10);
                          const month = parseInt(monthStr, 10);
                          const attendanceForMonth = attendance.filter((a) => String(a.date || '').startsWith(monthKey));
                          const monthLabel = new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
                          const cells = buildMonthCalendar(year, month, attendanceForMonth, approvedLeavesForCalendar, holidays);

                          return (
                            <div key={monthKey} className="report-month-group">
                              <h4>{monthLabel}</h4>
                              <div className="cal-grid">
                                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                                  <div key={d} className="cal-day-name">{d}</div>
                                ))}
                                {cells.map((cell, i) => {
                                  if (!cell) return <div key={`empty-${i}`} />;
                                  const { leave, holiday, record } = cell;
                                  const status = record?.status;
                                  const isWeekend = i % 7 === 0 || i % 7 === 6;
                                  let cellType = '';
                                  let tagText = '';
                                  let style = {};
                                  if (holiday) {
                                    cellType = 'holiday';
                                    tagText = holiday.name;
                                    style = { background: '#F0F5FF', color: '#2563eb', border: '1.5px solid #2563eb' };
                                  } else if (leave) {
                                    cellType = 'leave';
                                    tagText = 'LEAVE';
                                    style = { background: '#FEF9C3', color: '#B45309', border: '1.5px solid #F59E0B' };
                                  } else if (status) {
                                    cellType = status;
                                    tagText = CAL_STATUS_TAG[status] || 'STATUS';
                                    style = CAL_STATUS_STYLE[status] || {};
                                  } else if (isWeekend) {
                                    cellType = 'weekend';
                                    tagText = 'WEEKEND';
                                    style = { background: '#F3F4F6', color: '#6B7280' };
                                  }
                                  return (
                                    <div
                                      key={cell.dateStr}
                                      className={`cal-day cal-day--${cellType}`}
                                      style={{ ...style, position: 'relative' }}
                                    >
                                      <span>{cell.day}</span>
                                      {tagText && (
                                        <span className={`cal-day-tag ${cellType === 'holiday' ? 'cal-day-tag--holiday' : ''}`}>
                                          {tagText}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        });
                      })()}
                      <div className="cal-legend">
                        {CAL_STATUS_OPTIONS.map((o) => (
                          <span key={o.value} className="cal-legend-item"><span className="cal-legend-dot" style={{ background: o.color }} />{o.label}</span>
                        ))}
                        <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: '#F59E0B' }} />Leave</span>
                        <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: '#2563eb' }} />Holiday</span>
                        <span className="cal-legend-item"><span className="cal-legend-dot" style={{ background: '#6B7280' }} />Weekend</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLeaveBalanceVerification;
